import React, { useState, useEffect } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { GroupsService } from '../../services/groupsService';
import { CoachesService } from '../../services/coachesService';
import { GroupChatModel, WelcomeMessageModel, CoachModel } from '../../types/group';
import AddGroupDialog from './AddGroupDialog';
import EditGroupDialog from './EditGroupDialog';
import DeleteGroupDialog from './DeleteGroupDialog';

import { Plus, Trash2, Edit } from 'lucide-react';

import { toast } from 'react-toastify';

const Groups = () => {
  const [groups, setGroups] = useState<GroupChatModel[]>([]);
  const [coaches, setCoaches] = useState<CoachModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 10;

  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);
  const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<GroupChatModel | null>(null);

  useEffect(() => {
    loadGroups();
    loadCoaches();
  }, []);

  const loadGroups = async () => {
    try {
      setIsLoading(true);
      const groupsData = await GroupsService.loadGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
      toast.error('Failed to load groups. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCoaches = async () => {
    try {
      const coachesData = await CoachesService.getAllCoaches();
      setCoaches(coachesData);
    } catch (error) {
      console.error('Error loading coaches:', error);
    }
  };

  const handleAddGroup = () => {
    setShowAddGroupDialog(true);
  };

  const handleEditGroup = (group: GroupChatModel) => {
    setSelectedGroup(group);
    setShowEditGroupDialog(true);
  };

  const handleDeleteGroup = (group: GroupChatModel) => {
    setSelectedGroup(group);
    setShowDeleteGroupDialog(true);
  };

  const handleSaveGroup = async (group: GroupChatModel, welcomeMessage: WelcomeMessageModel) => {
    try {
      await GroupsService.createGroup(group);
      await GroupsService.sendWelcomeMessage(group.id, welcomeMessage);
      await loadGroups();
      toast.success('Group created successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error('Failed to create group. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleUpdateGroup = async (updatedGroup: GroupChatModel) => {
    try {
      await GroupsService.updateGroup(updatedGroup);
      await loadGroups();
      toast.success('Group updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error('Failed to update group. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleDeleteGroupConfirm = async () => {
    if (selectedGroup) {
      try {
        await GroupsService.deleteGroup(selectedGroup.id);
        await loadGroups();
        toast.success('Group deleted successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (error) {
        console.error('Error deleting group:', error);
        toast.error('Failed to delete group. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        throw error;
      }
    }
  };

  const handleCloseAddGroupDialog = () => setShowAddGroupDialog(false);
  const handleCloseEditGroupDialog = () => {
    setShowEditGroupDialog(false);
    setSelectedGroup(null);
  };
  const handleCloseDeleteGroupDialog = () => {
    setShowDeleteGroupDialog(false);
    setSelectedGroup(null);
  };

  const getModeratorName = (moderatorId: string) => {
    const coach = coaches.find(c => c.id === moderatorId);
    return coach ? coach.fullName : moderatorId;
  };

  const getModeratorNames = (moderatorIds: string[]) => {
    if (!moderatorIds || moderatorIds.length === 0) return 'No moderators';
    return moderatorIds
      .map(id => getModeratorName(id))
      .join(', ');
  };

  return (
    <React.Fragment>
      <BreadCrumb title='Groups' pageTitle='Dashboard' />
      <div className="col-span-12 card">
        <div className="card-body">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h6 className="text-15 font-semibold">
                All Groups
              </h6>
              <span className="bg-slate-100 dark:bg-zink-600 text-slate-600 dark:text-zink-200 px-2 py-1 rounded-full text-xs">
                {groups.length} groups
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleAddGroup}
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                <Plus className="inline-block size-4 mr-1" /> <span className="align-middle hidden sm:inline">Create New Group</span>
              </button>
            </div>
          </div>
        </div>

        <div className="!pt-0 card-body">
          {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-500 dark:text-zink-200">No groups found</p>
              </div>
            ) : (
              <div className="overflow-hidden">
                {/* Desktop View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600">
                      <tr>
                        <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-left w-80">
                          Group
                        </th>
                        <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-left">
                          Moderator
                        </th>
                        <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-center">
                          Status
                        </th>
                        <th className="px-4 py-3 first:pl-6 last:pr-6 font-semibold border-y border-slate-200 dark:border-zink-500 text-center w-32">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((group) => (
                        <tr key={group.id} className="border-t border-slate-200 dark:border-zink-500 hover:bg-slate-50 dark:hover:bg-zink-700/50">
                          <td className="px-4 py-3 first:pl-6 last:pr-6 w-80">
                            <div className="flex items-center gap-3">
                              <div className="size-10 rounded-full bg-green-100 dark:bg-green-500/20 shrink-0">
                                <img
                                  src={group.photoUrl}
                                  alt=""
                                  className="h-10 w-10 rounded-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (nextElement) nextElement.style.display = 'flex';
                                  }}
                                />
                                <div className="size-10 rounded-full bg-gray-100 dark:bg-gray-500/20 items-center justify-center hidden">
                                  <span className="text-gray-500">👥</span>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="break-words font-medium text-slate-800 dark:text-zink-100">
                                  {group.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 first:pl-6 last:pr-6">
                            <div className="break-words">
                              {getModeratorNames(group.moderators)}
                            </div>
                          </td>
                          <td className="px-4 py-3 first:pl-6 last:pr-6 text-center">
                            {group.status === 'active' ? (
                              <span className="px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-green-100 border-green-200 text-green-500 dark:bg-green-500/20 dark:border-green-500/20">
                                Active
                              </span>
                            ) : (
                              <span className="px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-red-100 border-red-200 text-red-500 dark:bg-red-500/20 dark:border-red-500/20">
                                Locked
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 first:pl-6 last:pr-6">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEditGroup(group)}
                                className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-blue-500 dark:hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/20"
                                title="Edit group"
                              >
                                <Edit className="size-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteGroup(group)}
                                className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                                title="Delete group"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile/Tablet View */}
                <div className="block md:hidden space-y-4">
                  {groups.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((group) => (
                    <div key={group.id} className="bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-500 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="size-12 rounded-full bg-green-100 dark:bg-green-500/20 shrink-0">
                            <img
                              src={group.photoUrl}
                              alt=""
                              className="h-12 w-12 rounded-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                if (nextElement) nextElement.style.display = 'flex';
                              }}
                            />
                            <div className="size-12 rounded-full bg-gray-100 dark:bg-gray-500/20 items-center justify-center hidden">
                              <span className="text-gray-500 text-lg">👥</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h6 className="font-medium text-slate-800 dark:text-zink-100 break-words">
                              {group.name}
                            </h6>
                            <p className="text-sm text-slate-600 dark:text-zink-300 break-words">
                              Moderators: {getModeratorNames(group.moderators)}
                            </p>
                            <div className="mt-1">
                              {group.status === 'active' ? (
                                <span className="px-2 py-0.5 text-xs inline-block font-medium rounded border bg-green-100 border-green-200 text-green-500 dark:bg-green-500/20 dark:border-green-500/20">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs inline-block font-medium rounded border bg-red-100 border-red-200 text-red-500 dark:bg-red-500/20 dark:border-red-500/20">
                                  Locked
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditGroup(group)}
                          className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-blue-500 dark:hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/20"
                          title="Edit group"
                        >
                          <Edit className="size-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group)}
                          className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                          title="Delete group"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isLoading && groups.length > 0 && (
              <div className="flex flex-col items-center justify-between mt-5 md:flex-row">
                <div className="mb-4 md:mb-0">
                  <p className="text-slate-500 dark:text-zink-200">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, groups.length)} of {groups.length} Results
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zink-700 dark:border-zink-500 dark:hover:bg-zink-600"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-600 dark:text-zink-300">
                    Page {currentPage} of {Math.ceil(groups.length / pageSize)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(groups.length / pageSize), prev + 1))}
                    disabled={currentPage === Math.ceil(groups.length / pageSize)}
                    className="px-3 py-1 text-sm bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zink-700 dark:border-zink-500 dark:hover:bg-zink-600"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      <AddGroupDialog
        onClose={handleCloseAddGroupDialog}
        onSave={handleSaveGroup}
        show={showAddGroupDialog}
        coaches={coaches}
      />

      {selectedGroup && (
        <EditGroupDialog
          group={selectedGroup}
          onClose={handleCloseEditGroupDialog}
          onSave={handleUpdateGroup}
          show={showEditGroupDialog}
          coaches={coaches}
        />
      )}

      {selectedGroup && (
        <DeleteGroupDialog
          group={selectedGroup}
          onClose={handleCloseDeleteGroupDialog}
          onDelete={handleDeleteGroupConfirm}
          show={showDeleteGroupDialog}
        />
      )}
    </React.Fragment>
  );
};

export default Groups;